import { DECODED_ACCESS_TOKEN } from "../../constants/system.keys";
import { beeXLogger } from "../../helpers/logger";
import storageService from "../../services/storage.service";

export const DOWNLOAD_TYPE = {
  BEEX_HTTP: 'beex_http',
};

class Downloader {
  downloads = new Map();
  callbacks = new Set();
  uniqueDownloadingKey = new Set();

  constructor() {
    this.downloads = new Map();
    this.callbacks = new Set();
    this.uniqueDownloadingKey = new Set();
  }

  subscribe(callback) {
    this.callbacks.add(callback);
    return () => this.callbacks.delete(callback);
  }

  notify(key) {
    const state = this.downloads.get(key);
    if (!state) return;
    this.callbacks.forEach((cb) => cb({ key, state }));
  }

  addToWaitList(key) {
    this.downloads.set(key, { status: 'waiting' });
    this.notify(key);
  }

  async download(key, url, downloadType = null, fileSize = 0) {
    if (this.uniqueDownloadingKey.has(key)) {
      beeXLogger.debug('Already downloading:', key);
      return;
    }
    this.uniqueDownloadingKey.add(key);

    const controller = new AbortController();

    this.downloads.set(key, {
      key,
      url,
      progress: 0,
      blob: null,
      error: null,
      status: 'downloading',
      controller,
      downloadSize: 0,
      totalSize: 0,
    });
    this.notify(key);

    try {
      let requestConfig = {
        method: 'GET',
        signal: controller.signal,
      };

      if (downloadType === DOWNLOAD_TYPE.BEEX_HTTP) {
        const decodedAccessToken =
          storageService.getInternalStorageByKey(DECODED_ACCESS_TOKEN);

        requestConfig.headers = {
          Authorization: decodedAccessToken,
          Connection: 'keep-alive',
          // 'Keep-Alive': 'timeout=300',
        };
        requestConfig.keepalive = true;
      }

      const response = await fetch(url, requestConfig);
      beeXLogger.debug('FETCH DOWNLOAD RESPONSE:', response);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      console.log('All headers:', [...response.headers.entries()]);
      const contentLength = response.headers.get('content-length');
      console.log('contentLength: ', contentLength);
      const total = fileSize
        ? fileSize
        : contentLength
          ? parseInt(contentLength, 10)
          : 0;

      if (downloadType === DOWNLOAD_TYPE.BEEX_HTTP) {
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('Reader not supported');
        }

        let received = 0;
        const chunks = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const prev = this.downloads.get(key);
          if (!prev) return;
          if (received + value.length >= fileSize) {
            this.downloads.set(key, {
              ...prev,
              progress: 100,
              downloadSize: fileSize,
              totalSize: fileSize || total || received,
            });
            break;
          }
          received += value.length;
          chunks.push(value);

          const progress = total
            ? (received / total) * 100
            : (prev.progress ?? 0);

          this.downloads.set(key, {
            ...prev,
            progress,
            downloadSize: received,
            totalSize: fileSize || total || received,
          });

          this.notify(key);
        }

        const blob = new Blob(chunks);

        this.downloads.set(key, {
          key,
          url,
          progress: 100,
          blob,
          error: null,
          status: 'completed',
          controller: undefined,
          downloadSize: received,
          totalSize: total || received,
        });

        this.notify(key);
        return;
      }

      const reader = response.body?.getReader();

      if (reader && total > 0) {
        let received = 0;
        const chunks = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          received += value.length;
          chunks.push(value);

          const prev = this.downloads.get(key);
          if (!prev) return;

          const progress = (received / total) * 100;

          this.downloads.set(key, {
            ...prev,
            progress: Math.max(prev.progress ?? 0, progress),
            downloadSize: received,
            totalSize: total,
          });

          this.notify(key);
        }

        const blob = new Blob(chunks);

        this.downloads.set(key, {
          key,
          url,
          progress: 100,
          blob,
          error: null,
          status: 'completed',
          controller: undefined,
          downloadSize: received,
          totalSize: total,
        });

        this.notify(key);
        return;
      }

      const blob = await response.blob();

      this.downloads.set(key, {
        key,
        url,
        progress: 100,
        blob,
        error: null,
        status: 'completed',
        controller: undefined,
        downloadSize: blob.size,
        totalSize: blob.size,
      });

      this.notify(key);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        this.downloads.delete(key);
        this.notify(key);
        return;
      }

      const prev = this.downloads.get(key);

      this.downloads.set(key, {
        ...prev,
        error: error instanceof Error ? error : new Error('Unknown error'),
        status: 'error',
        controller: undefined,
      });

      this.notify(key);
    } finally {
      this.uniqueDownloadingKey.delete(key);
    }
  }

  getState(key) {
    return this.downloads.get(key);
  }

  getBlob(key) {
    return this.downloads.get(key)?.blob ?? null;
  }

  cancel(key) {
    const state = this.downloads.get(key);
    if (state?.controller) {
      state.controller.abort();
    }
    this.uniqueDownloadingKey.delete(key);
    this.downloads.delete(key);
    this.notify(key);
  }

  query(predicate) {
    const results = [];
    this.downloads.forEach((state, key) => {
      if (predicate(key)) {
        results.push({ key, state });
      }
    });
    return results;
  }

  getAllStates() {
    return Array.from(this.downloads.entries()).map(([key, state]) => ({
      key,
      state,
    }));
  }

  static downloadBlob(blob, filename) {
    const a = document.createElement('a');
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }
}

export const downloaderInstance = new Downloader();
export default Downloader;
